import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "reactstrap";
import CommonDataTable from "../../common/DataTable";
import { getWithdrawalUsers } from "../../helpers/apis";
import { toast } from "react-toastify";
import AddCoinsModal from "./AddCoinsModal";
import RemoveCoinsModal from "./RemoveCoinsModal";
import WithdrawalHistoryModal from "./WithdrawalHistoryModal";
import { formatDateTimeDDMMYYYY } from "../../helpers/dateUtils";

const Withdrawal = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    const [filters, setFilters] = useState({
        page: 1,
        limit: 10,
        search: ""
    });
    const [selectedUser, setSelectedUser] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const isMounted = useRef(true);

    const fetchUsers = useCallback(async () => {
        if (!isMounted.current) return;
        
        setLoading(true);
        try {
            const response = await getWithdrawalUsers(filters);
            
            // The axios interceptor returns response.data directly, so response is already the data object
            if (response && response.status === 1) {
                const usersData = response.data?.users || [];
                setUsers(usersData);
                const paginationData = response.data?.pagination || {};
                setPagination({
                    page: paginationData.currentPage || filters.page,
                    limit: paginationData.limit || filters.limit,
                    total: paginationData.totalUsers || 0,
                    totalPages: paginationData.totalPages || 0
                });
            } else {
                toast.error(response?.message || "Failed to fetch users");
            }
        } catch (error) {
            console.error("Error fetching withdrawal users:", error);
            toast.error(error.message || "Failed to fetch users");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handlePageChange = (page) => {
        setFilters(prev => ({ ...prev, page }));
    };

    const handleRowsPerPageChange = (limit) => {
        setFilters(prev => ({ ...prev, page: 1, limit }));
    };

    const updateFilterParams = useCallback((newParams) => {
        setFilters(prev => ({ ...prev, ...newParams }));
    }, []);

    const handleAddCoins = (user) => {
        setSelectedUser(user);
        setShowAddModal(true);
    };

    const handleRemoveCoins = (user) => {
        setSelectedUser(user);
        setShowRemoveModal(true);
    };

    const handleViewHistory = (user) => {
        setSelectedUser(user);
        setShowHistoryModal(true);
    };

    const handleModalClose = () => {
        setSelectedUser(null);
        setShowAddModal(false);
        setShowRemoveModal(false);
        setShowHistoryModal(false);
        // Refresh users list after modal closes
        fetchUsers();
    };

    const columns = [
        {
            name: "Account No.",
            selector: row => row.acnumber || 'N/A',
            sortable: true,
        },
        {
            name: "Current Coins",
            selector: row => row.currentCoins || 0,
            sortable: true,
            cell: row => (
                <span className="badge bg-info fs-6 px-3 py-2">
                    {row.currentCoins || 0}
                </span>
            )
        },
        {
            name: "Total Added",
            selector: row => row.totalAdded || 0,
            sortable: true,
            cell: row => (
                <span className="text-success fw-semibold">
                    {row.totalAdded || 0}
                </span>
            )
        },
        {
            name: "Total Removed",
            selector: row => row.totalRemoved || 0,
            sortable: true,
            cell: row => (
                <span className="text-danger fw-semibold">
                    {row.totalRemoved || 0}
                </span>
            )
        },
        {
            name: "Updated At",
            selector: row => row.updatedAt ? formatDateTimeDDMMYYYY(row.updatedAt) : 'N/A',
            sortable: true,
            cell: row => (
                <small className="text-muted">
                    {row.updatedAt ? formatDateTimeDDMMYYYY(row.updatedAt) : 'N/A'}
                </small>
            )
        },
        {
            name: "Actions",
            cell: row => (
                <div className="d-flex gap-2">
                    <Button
                        color="success"
                        size="sm"
                        onClick={() => handleAddCoins(row)}
                        title="Add Coins"
                    >
                        <i className="ri-add-line"></i>
                    </Button>
                    <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleRemoveCoins(row)}
                        title="Remove Coins"
                    >
                        <i className="ri-subtract-line"></i>
                    </Button>
                    <Button
                        color="info"
                        size="sm"
                        onClick={() => handleViewHistory(row)}
                        title="View History"
                    >
                        <i className="ri-history-line"></i>
                    </Button>
                </div>
            ),
            ignoreRowClick: true
        }
    ];

    return (
        <div className="page-content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="page-title-box d-flex align-items-center justify-content-between">
                            <h4 className="mb-0">
                                <i className="ri-wallet-3-line me-2"></i>
                                Withdrawal Management
                            </h4>
                        </div>
                    </div>
                </div>

                <CommonDataTable
                    columns={columns}
                    data={users}
                    loading={loading}
                    totalRows={pagination.total}
                    filterParams={filters}
                    fetchData={updateFilterParams}
                    searchEnable={true}
                    showAddButton={false}
                    showExportButton={false}
                    checkboxEnabled={false}
                    paginationPerPage={filters.limit}
                    paginationDefaultPage={filters.page}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    showRefreshButton={true}
                    onRefresh={fetchUsers}
                    refreshLoading={loading}
                    striped
                    highlightOnHover
                    conditionalRowStyles={[
                        {
                            when: (row) => row.isDeleted || row.status === 'deleted' || row.userStatus === 'deleted',
                            style: {
                                opacity: 0.5,
                                backgroundColor: '#f9f9f9',
                                cursor: 'not-allowed'
                            }
                        }
                    ]}
                />

                {/* Add Coins Modal */}
                <AddCoinsModal
                    isOpen={showAddModal}
                    toggle={handleModalClose}
                    user={selectedUser}
                    onSuccess={handleModalClose}
                />

                {/* Remove Coins Modal */}
                <RemoveCoinsModal
                    isOpen={showRemoveModal}
                    toggle={handleModalClose}
                    user={selectedUser}
                    onSuccess={handleModalClose}
                />

                {/* History Modal */}
                <WithdrawalHistoryModal
                    isOpen={showHistoryModal}
                    toggle={handleModalClose}
                    user={selectedUser}
                />
            </div>
        </div>
    );
};

export default Withdrawal;

